import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

/**
 * Keep `document.title` describing the current route (WCAG 2.1 — 2.4.2 Page
 * Titled, Level A).
 *
 * As an SPA the app shipped a single static `<title>` that never changed, so
 * every route announced identically. The title is the first thing a screen
 * reader reads on navigation and the only thing distinguishing entries in the
 * browser's tab strip, history and window switcher — with one value, none of
 * those work.
 *
 * Route-pattern driven rather than per-page hook calls: 30+ routes each
 * remembering to set their own title is a rule that decays, and a missed one
 * silently inherits whatever the previous page set. One ordered table is
 * greppable and fails visibly.
 */

/** First match wins, so put specific patterns before their prefixes. */
const TITLE_PATTERNS: ReadonlyArray<[RegExp, string]> = [
  [/^\/auth\/logout/, 'a11y.pageTitles.signOut'],
  [/^\/auth\/logged-out/, 'a11y.pageTitles.signedOut'],
  [/^\/auth\/dev-login/, 'a11y.pageTitles.signIn'],
  [/^\/auth\/error/, 'a11y.pageTitles.signInError'],

  [/^\/approver\/audit-history/, 'a11y.pageTitles.auditHistory'],
  [/^\/approver\/archive/, 'a11y.pageTitles.archive'],
  [/^\/approver\/workflows\/[^/]+\/preview/, 'a11y.pageTitles.preview'],
  [/^\/approver\/workflows\/[^/]+\/versions\/[^/]+/, 'a11y.pageTitles.versionDetails'],
  [/^\/approver\/workflows\/[^/]+\/versions/, 'a11y.pageTitles.versionHistory'],
  [/^\/approver\/workflows\/[^/]+\/steps\/[^/]+\/config/, 'a11y.pageTitles.stepConfig'],
  [/^\/approver\/workflows\/[^/]+/, 'a11y.pageTitles.workflowReview'],
  [/^\/approver\/dashboard/, 'a11y.pageTitles.approverDashboard'],

  [/^\/originator\/audit-history/, 'a11y.pageTitles.auditHistory'],
  [/^\/originator\/archive/, 'a11y.pageTitles.archive'],
  [/^\/originator\/custom-elements/, 'a11y.pageTitles.customElements'],
  [/^\/originator\/workflows\/[^/]+\/preview/, 'a11y.pageTitles.preview'],
  [/^\/originator\/workflows\/[^/]+\/pdf-mapping/, 'a11y.pageTitles.pdfMapping'],
  [/^\/originator\/workflows\/[^/]+\/versions\/[^/]+/, 'a11y.pageTitles.versionDetails'],
  [/^\/originator\/workflows\/[^/]+\/versions/, 'a11y.pageTitles.versionHistory'],
  [/^\/originator\/workflows\/[^/]+\/steps\/[^/]+\/edit/, 'a11y.pageTitles.formBuilder'],
  [/^\/originator\/workflows\/[^/]+\/steps\/[^/]+\/config/, 'a11y.pageTitles.stepConfig'],
  [/^\/originator\/workflows\/[^/]+/, 'a11y.pageTitles.workflowDetails'],
  [/^\/originator\/dashboard/, 'a11y.pageTitles.originatorDashboard'],

  [/^\/advisor\/workflows\/[^/]+\/applications/, 'a11y.pageTitles.application'],
  [/^\/advisor\/dashboard/, 'a11y.pageTitles.advisorDashboard'],

  [/^\/health/, 'a11y.pageTitles.health'],
  [/^\/$/, 'a11y.pageTitles.home'],
];

/** Exported for the test — the mapping is the part worth pinning. */
export function titleKeyForPath(pathname: string): string {
  for (const [pattern, key] of TITLE_PATTERNS) {
    if (pattern.test(pathname)) return key;
  }
  return 'a11y.pageTitles.notFound';
}

export function useDocumentTitle(): void {
  const { pathname } = useLocation();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    // Suffixed with the product name so the tab strip still identifies the app
    // when the page name alone is generic ("Archive", "Preview").
    document.title = `${t(titleKeyForPath(pathname))} — ${t('app.title')}`;
    // i18n.language so the title re-resolves on a language switch rather than
    // sitting in the outgoing locale until the next navigation.
  }, [pathname, t, i18n.language]);
}

/** Zero-render component wrapper — lets the hook be mounted declaratively
 *  inside the Router without giving App a body it doesn't otherwise need. */
export function DocumentTitle(): null {
  useDocumentTitle();
  return null;
}
